import React, { useMemo } from 'react';
import { useOrganizationContext } from '../../context/OrganizationContext';
import { getHashMap } from '../../utils/objectTransformers';
import { ValidationErrors } from '../../hooks/useForm';
import { Branch } from '../../types/branch';
import { ALL_SELECT_OPTION, getBranchOptions } from '../../utils/selectOptions';
import { AdaptiveSelect } from '../AdaptiveSelect/AdaptiveSelect';
import { classNames } from 'primereact/utils';
import { useAuthContext } from '../../context/AuthContext';
import { ALL_FEATURES } from '../../config/features';

interface BaseProps {
  onChange: ((branches: Branch[]) => void) | ((branch: Branch | undefined) => void);
  value?: string[] | string;
  id?: string;
  error?: string | (ValidationErrors<Branch> | undefined)[];
  className?: string;
  placeholder?: string;
  searchPlaceholder?: string;
  isSingle: boolean;
  selectAllOption?: boolean;
  excludeBranchIds?: string[];
  filterByBranchIds?: string[];
  onlyAuthUserBranches?: boolean;
  disabled?: boolean;
}

interface MultiProps extends BaseProps {
  isSingle: false;
  onChange: (branches: Branch[]) => void;
  value?: string[];
  selectAllOption?: never;
}

interface SingleSelectProps extends BaseProps {
  isSingle: true;
  onChange: (branch: Branch | undefined) => void;
  value?: string;
  selectAllOption?: boolean;
}

type Props = MultiProps | SingleSelectProps;

export const BranchesSelect = ({
  onChange,
  id = 'branches-dropdown',
  value,
  error,
  className,
  placeholder,
  searchPlaceholder = 'Buscar sucursales',
  isSingle,
  selectAllOption = false,
  excludeBranchIds = [],
  filterByBranchIds,
  onlyAuthUserBranches = false,
  disabled = false,
}: Props) => {
  const { orgBranches, organizationLoading } = useOrganizationContext();
  const { user, features } = useAuthContext();

  const finalPlaceholder = placeholder ?? (isSingle ? 'Elige sucursal' : 'Elige sucursales');

  const branchesHashMap = useMemo(() => getHashMap(orgBranches ?? [], 'id'), [orgBranches]);

  const options = useMemo(() => {
    const filteredBranches = orgBranches?.filter(
      (branch) =>
        !excludeBranchIds.includes(branch.id) && (filterByBranchIds === undefined || filterByBranchIds.includes(branch.id)),
    );
    const shouldRestrictBranches = onlyAuthUserBranches && !features?.includes(ALL_FEATURES.ORG_ADMIN);
    const branchesAvailableToUser = shouldRestrictBranches
      ? filteredBranches?.filter((branch) => user?.branches?.some((b) => b.id === branch.id))
      : filteredBranches;
    return getBranchOptions(branchesAvailableToUser, selectAllOption);
  }, [excludeBranchIds, filterByBranchIds, orgBranches, onlyAuthUserBranches, features, selectAllOption, user?.branches]);

  const handleChange = (newValue: any) => {
    if (!isSingle) {
      const newBranches: Branch[] = (newValue as string[]).map((branchId: string) => branchesHashMap[branchId]) as Branch[];
      onChange(newBranches);
      return;
    }
    if (newValue === ALL_SELECT_OPTION) {
      onChange(undefined);
      return;
    }
    onChange(branchesHashMap[newValue as string]);
  };

  const branchTemplate = (option: any) => {
    return <span>{option.label}</span>;
  };

  return (
    <AdaptiveSelect
      id={id}
      isMultiple={!isSingle}
      value={value}
      onChange={handleChange}
      options={options}
      itemTemplate={branchTemplate}
      placeholder={finalPlaceholder}
      modalTitle={isSingle ? 'Seleccionar sucursal' : 'Seleccionar sucursales'}
      searchPlaceholder={searchPlaceholder}
      className={classNames(className, { 'p-invalid': error })}
      invalid={!!error}
      loading={organizationLoading}
      disabled={disabled}
      emptyMessage='No hay sucursales'
      emptyFilterMessage='No se han encontrado sucursales'
    />
  );
};
